import { useCallback, useEffect, useRef } from "react";

const TITLE_DRAG_THRESHOLD = 4;

export const useWindowPointer = ({
  id,
  windowState,
  isMobile,
  move,
  resize,
  focus,
}) => {
  const sessionRef = useRef(null);
  const stateRef = useRef(windowState);
  stateRef.current = windowState;

  const endSession = useCallback(() => {
    sessionRef.current = null;
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  useEffect(() => {
    const onMove = (event) => {
      const session = sessionRef.current;

      if (!session) {
        return;
      }

      if (session.type === "drag") {
        if (session.wasMaximized) {
          const dx = event.clientX - session.startX;
          const dy = event.clientY - session.startY;

          if (
            Math.abs(dx) < TITLE_DRAG_THRESHOLD &&
            Math.abs(dy) < TITLE_DRAG_THRESHOLD
          ) {
            return;
          }

          const restore = session.restoreRect || session.origin;
          session.origin = {
            ...restore,
            x: event.clientX - restore.width / 2,
            y: 0,
          };
          session.startX = event.clientX;
          session.startY = event.clientY;
          session.wasMaximized = false;
        }

        move(
          id,
          session.origin.x + event.clientX - session.startX,
          session.origin.y + event.clientY - session.startY,
          {
            width: session.origin.width,
            height: session.origin.height,
          }
        );
        return;
      }

      if (session.type === "resize") {
        const { origin, startX, startY, edge } = session;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        let x = origin.x;
        let y = origin.y;
        let width = origin.width;
        let height = origin.height;

        if (edge.includes("e")) {
          width = origin.width + dx;
        }

        if (edge.includes("s")) {
          height = origin.height + dy;
        }

        if (edge.includes("w")) {
          width = origin.width - dx;
          x = origin.x + dx;
        }

        if (edge.includes("n")) {
          height = origin.height - dy;
          y = origin.y + dy;
        }

        resize(id, { x, y, width, height }, edge);
      }
    };

    const onUp = () => endSession();

    window.addEventListener("pointermove", onMove, true);
    window.addEventListener("pointerup", onUp, true);
    window.addEventListener("pointercancel", onUp, true);

    return () => {
      window.removeEventListener("pointermove", onMove, true);
      window.removeEventListener("pointerup", onUp, true);
      window.removeEventListener("pointercancel", onUp, true);
    };
  }, [endSession, id, move, resize]);

  const onTitlePointerDown = useCallback(
    (event) => {
      if (event.button !== 0) {
        return;
      }

      const target =
        event.target instanceof Element
          ? event.target
          : event.target.parentElement;

      if (target?.closest("[data-window-control]")) {
        return;
      }

      event.preventDefault();
      focus(id);

      const win = stateRef.current;

      if (event.pointerId != null) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      document.body.style.userSelect = "none";
      sessionRef.current = {
        type: "drag",
        startX: event.clientX,
        startY: event.clientY,
        origin: {
          x: win.x,
          y: win.y,
          width: win.width,
          height: win.height,
        },
        wasMaximized: win.isMaximized,
        restoreRect: win.restoreRect,
      };
    },
    [focus, id]
  );

  const onResizePointerDown = useCallback(
    (event, edge) => {
      if (event.button !== 0) {
        return;
      }

      event.stopPropagation();
      event.preventDefault();
      focus(id);

      if (isMobile) {
        return;
      }

      const win = stateRef.current;

      if (win.isMaximized) {
        return;
      }

      if (event.pointerId != null) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      document.body.style.userSelect = "none";
      sessionRef.current = {
        type: "resize",
        edge,
        startX: event.clientX,
        startY: event.clientY,
        origin: {
          x: win.x,
          y: win.y,
          width: win.width,
          height: win.height,
        },
      };
    },
    [focus, id, isMobile]
  );

  return { onTitlePointerDown, onResizePointerDown };
};
